import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NavStorageService } from 'src/app/core/nav-storage/services/nav-storage.service';
import { SendInfoComponentService } from 'src/app/core/services/local/send-info.component.service';
import { SnackbarService } from 'src/app/core/shared/components/snackbar/snackbar.service';
import { InfoHelper } from 'src/app/core/shared/helpers/info.helper';
import { NavigationHelper } from 'src/app/core/shared/helpers/navigation.helper';
import { DefaultConfigComponent } from 'src/app/core/shared/inherit/default-config.component';
import { TranslateHelper } from '../../helpers/translate.helper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from 'src/app/core/pipes/translate.pipe';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'app-not-found',
  templateUrl: './not-found.component.html',
  styleUrls: ['./not-found.component.scss'],
  imports: [MatCardModule, MatButtonModule, MatIconModule, TranslatePipe],
  standalone: true,
})
export class NotFoundComponent extends DefaultConfigComponent<void> implements OnInit {
  // Constante global para timeout
  static readonly DEFAULT_TIMEOUT = 10000; // 10 segundos

  showMenu: boolean = true;

  // Propiedades para mostrar detalles de error
  errorType: string | null = null;
  errorDetails: any = null;
  errorMessages = {
    resourceUnavailable: 'Recurso no disponible',
    timeout: 'Tiempo de espera excedido',
    actionError: 'Error en la acción'
  };

  private static translateHelper: TranslateHelper;

  private static _listTraduction: any = null;

  constructor(
    public breakpointObserver: BreakpointObserver,
    snackBar: SnackbarService,
    infoHelper: InfoHelper,
    navStorageService: NavStorageService,
    nav: Router,
    private route: ActivatedRoute,
    executeButtonService: SendInfoComponentService,
  ) {
    super(
      snackBar,
      infoHelper,
      navStorageService,
      nav,
      NavigationHelper.createTab(nav.url, "Página no encontrada"),
      executeButtonService
    );
  }

  static setTranslations(translations: any) {
    NotFoundComponent._listTraduction = translations;
  }

  static getTranslationStatic(key: string, type: 'lbl' | 'ph' | 'msg' = 'lbl'): string {
    if (NotFoundComponent._listTraduction && NotFoundComponent._listTraduction[type]) {
      return NotFoundComponent._listTraduction[type].get(key) || key;
    }
    return key;
  }

  initializeTranslateHelper(translateHelper: TranslateHelper) {
    NotFoundComponent.translateHelper = translateHelper;
  }

  mostrarMenu() {
    this.showMenu = !this.showMenu;
  }

  ngOnInit() {
    this.onListenTranslate('loading');

    this.breakpointObserver
      .observe(['(min-width: 992px)'])
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.showMenu = true;
        } else {
          this.showMenu = false;
        }
      });

    // Verificar parámetros de error en la URL
    this.route.queryParams.subscribe(params => {
      if (params['errorType']) {
        this.errorType = params['errorType'];

        try {
          if (params['errorDetails']) {
            this.errorDetails = JSON.parse(decodeURIComponent(params['errorDetails']));
          }
        } catch (e) {
          console.error('Error al parsear detalles del error:', e);
          this.errorDetails = { message: 'Detalles del error no disponibles' };
        }
      }
    });
  }

/**
 * Caso 1: Maneja errores cuando el usuario ingresa a una pantalla habilitada 
 * para su perfil pero ocurre un problema técnico o de red
 * Este método es estático para poder llamarlo desde otros componentes
 * @param router Instancia del Router para la navegación
 * @param snackBar Servicio de notificaciones
 * @param error El error capturado
 * @param moduleName Nombre del módulo donde ocurrió el error
 * @param activatedRoute Ruta activada opcional para navegación relativa
 */
static handleResourceUnavailableError(
  router: Router,
  snackBar: SnackbarService,
  error: any,
  moduleName: string = '',
  activatedRoute?: ActivatedRoute
): void {
  const errorMessage = `Error: Ha ocurrido un problema técnico al cargar recursos de SANET. ${moduleName ? `Módulo: ${moduleName}` : ''}`;

  // Mostrar notificación
  //snackBar.openSnackBarDanger(errorMessage);
  console.error('Error técnico o de red - recursos no disponibles:', error);

  // Esperar un momento para que se muestre la notificación antes de navegar
  setTimeout(() => {
    if (activatedRoute) {
      // Usando navegación relativa como en tu ejemplo
      router.navigate(['../not-found'], { relativeTo: activatedRoute });
    } else {
      // Navegación absoluta como fallback
      router.navigate(['/not-found']);
    }
  }, 1000);
}

/**
 * Caso 2: Maneja errores por exceso en tiempo de respuesta durante consultas,
 * descargas de reportes, impresiones de documentos o consultas a servicios de terceros
 * Este método es estático para poder llamarlo desde otros componentes
 * @param router Instancia del Router para la navegación
 * @param snackBar Servicio de notificaciones
 * @param operation La operación que se estaba realizando
 * @param activatedRoute Ruta activada opcional para navegación relativa
 */
static handleTimeoutError(
  router: Router,
  snackBar: SnackbarService,
  operation: string,
  activatedRoute?: ActivatedRoute
): void {
  const errorMessage = 'Se superó el tiempo de espera de esta consulta, vuelve a intentar en unos momentos, si el problema persiste contacta con un administrador.';

  // Mostrar notificación
  snackBar.openSnackBarDanger(errorMessage);
  console.error(`Tiempo de espera excedido en la operación: ${operation}`);

  // Esperar un momento para que se muestre la notificación antes de navegar
  setTimeout(() => {
    if (activatedRoute) {
      // Usando navegación relativa como en tu ejemplo
      router.navigate(['../not-found'], { relativeTo: activatedRoute });
    } else {
      // Navegación absoluta como fallback
      router.navigate(['/not-found']);
    }
  }, 1000);
}

/**
 * Caso 3: Maneja errores cuando no puede realizarse una acción durante registros nuevos,
 * eliminación de registros, actualización de información, solicitud de servicios o peticiones a terceros
 * Este método es estático para poder llamarlo desde otros componentes
 * @param router Instancia del Router para la navegación
 * @param snackBar Servicio de notificaciones
 * @param action La acción que se intentaba realizar
 * @param error El error capturado
 * @param activatedRoute Ruta activada opcional para navegación relativa
 */
static handleActionError(
  router: Router,
  snackBar: SnackbarService,
  action: string,
  error: any,
  activatedRoute?: ActivatedRoute
): void {
  const errorMessage = 'La acción no puede realizarse con éxito. Intenta de nuevo... Si el error persiste contacta con un administrador.';

  // Mostrar notificación
  snackBar.openSnackBarDanger(errorMessage);
  console.error(`No se pudo realizar la acción '${action}':`, error);

  // Esperar un momento para que se muestre la notificación antes de navegar
  setTimeout(() => {
    if (activatedRoute) {
      // Usando navegación relativa como en tu ejemplo
      router.navigate(['../not-found'], { relativeTo: activatedRoute });
    } else {
      // Navegación absoluta como fallback
      router.navigate(['/not-found']);
    }
  }, 1000);
}

  /**
   * Determina el mensaje de error a mostrar según el tipo
   */
  getErrorMessage(): string {
    if (!this.errorType) return 'Ha ocurrido un error inesperado';

    const baseMessage = this.errorMessages[this.errorType as keyof typeof this.errorMessages] || 'Error inesperado';

    if (this.errorDetails && this.errorDetails.message) {
      return `${baseMessage}: ${this.errorDetails.message}`;
    }

    return baseMessage;
  }

  /**
   * Determina si debe mostrar detalles adicionales del error
   */
  shouldShowDetails(): boolean {
    return !!this.errorType && !!this.errorDetails;
  }

  /**
   * Obtiene detalles formatados del error para mostrar
   */
  getFormattedDetails(): any[] {
    if (!this.errorDetails) return [];

    const result = [];

    // Información común
    if (this.errorDetails.timestamp) {
      result.push({
        label: 'Fecha y hora:',
        value: new Date(this.errorDetails.timestamp).toLocaleString()
      });
    }

    // Información específica por tipo de error
    switch (this.errorType) {
      case 'resourceUnavailable':
        if (this.errorDetails.moduleName) {
          result.push({
            label: 'Módulo:',
            value: this.errorDetails.moduleName
          });
        }
        break;

      case 'timeout':
        if (this.errorDetails.operation) {
          result.push({
            label: 'Operación:',
            value: this.errorDetails.operation
          });
        }
        if (this.errorDetails.timeout) {
          result.push({
            label: 'Tiempo límite:',
            value: `${this.errorDetails.timeout / 1000} segundos`
          });
        }
        break;

      case 'actionError':
        if (this.errorDetails.action) {
          result.push({
            label: 'Acción:',
            value: this.errorDetails.action
          });
        }
        if (this.errorDetails.errorCode) {
          result.push({
            label: 'Código de error:',
            value: this.errorDetails.errorCode
          });
        }
        break;
    }

    return result;
  }

  /**
   * Vuelve a la página anterior
   */
  goBack(): void {
    window.history.back();
  }
  returnHome(){
    this.navRouter.navigateByUrl("/home");
  }
}